import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { ReminderTiming } from '@/hooks/useTodos';

interface ReminderPickerProps {
  value: ReminderTiming;
  onChange: (value: ReminderTiming) => void;
  children: React.ReactNode;
  className?: string; // Optional wrapper className
}

const ReminderPicker: React.FC<ReminderPickerProps> = ({ value, onChange, children, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customInput, setCustomInput] = useState('');

  const popupRef = useRef<HTMLDivElement>(null);

  // Update position when opening
  React.useLayoutEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popupWidth = 192; // w-48 = 12rem = 192px
      
      let popupHeight = 260; // Increased estimate for more options
      if (popupRef.current) {
          popupHeight = popupRef.current.offsetHeight;
      } else {
          // Fallback estimate
          popupHeight = showCustomInput ? 80 : 200; 
      }

      // Default: below the trigger
      let top = rect.bottom + 8;
      
      // Default: Align right edge of popup with right edge of trigger (since trigger is usually on right side)
      let left = rect.right - popupWidth;
      
      // Check bottom overflow - flip to top
      if (rect.bottom + popupHeight > window.innerHeight) {
          top = rect.top - popupHeight - 8;
      }
      
      // Check left overflow - if aligning right pushes it off screen left, align left instead
      if (left < 10) {
          left = rect.left;
      }
      
      // Check right overflow - if aligning left pushes it off screen right (rare given logic above), constrain it
      if (left + popupWidth > window.innerWidth) {
          left = window.innerWidth - popupWidth - 10;
      }

      setPosition({ top, left });
    } else {
        setShowCustomInput(false);
        setCustomInput('');
    }
  }, [isOpen, showCustomInput]);

  // Close on click outside or scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const portalContent = document.getElementById('reminder-portal-content');
      if (containerRef.current && !containerRef.current.contains(event.target as Node) && 
          portalContent && !portalContent.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    const handleScroll = (event: Event) => {
        const target = event.target as Node;
        const portalContent = document.getElementById('reminder-portal-content');
        // Prevent closing if scrolling inside the popup
        if (portalContent && portalContent.contains(target)) {
            return;
        }
        if (isOpen) setIsOpen(false);
    };

    if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', () => setIsOpen(false));
        // Use capture to detect scrolling in nested containers
        window.addEventListener('scroll', handleScroll, { capture: true });
    }
    
    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('resize', () => setIsOpen(false));
        window.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [isOpen]);

  const reminderOptions: { label: string, value: ReminderTiming }[] = [
    { label: '不提醒', value: null },
    { label: '提前10分钟', value: 10 },
    { label: '提前20分钟', value: 20 },
    { label: '提前30分钟', value: 30 },
    { label: '提前1小时', value: '1h' },
    { label: '提前12小时', value: '12h' },
    { label: '提前1天', value: '1d' },
  ];

  const handleCustomSubmit = () => {
      const mins = parseInt(customInput);
      if (!isNaN(mins) && mins > 0) {
          onChange(mins);
          setIsOpen(false);
      }
  };

  return (
    <div className={`relative inline-block ${className || ''}`} ref={containerRef}>
        <div onClick={() => setIsOpen(!isOpen)} className="cursor-pointer">
            {children}
        </div>

        {isOpen && createPortal(
            <AnimatePresence>
                <motion.div
                    ref={popupRef}
                    id="reminder-portal-content"
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    style={{ 
                        position: 'fixed',
                        top: position.top, 
                        left: position.left,
                        zIndex: 9999 
                    }}
                    className="bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 w-48 overflow-hidden py-1"
                >
                    {showCustomInput ? (
                        <div className="p-2">
                            <div className="text-xs text-slate-500 mb-1">提前多少分钟?</div>
                            <div className="flex gap-1">
                                <input 
                                    type="number"
                                    value={customInput}
                                    onChange={(e) => setCustomInput(e.target.value)}
                                    className="w-16 text-xs px-2 py-1 border border-slate-200 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-700 outline-none focus:border-blue-500 text-slate-900 dark:text-slate-100"
                                    autoFocus
                                    min={1}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleCustomSubmit();
                                    }}
                                />
                                <button 
                                    type="button"
                                    onClick={handleCustomSubmit}
                                    className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                >
                                    确定
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => setShowCustomInput(false)}
                                    className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                                >
                                    取消
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {reminderOptions.map(opt => (
                                <button
                                    key={opt.label}
                                    type="button"
                                    onClick={() => {
                                        onChange(opt.value);
                                        setIsOpen(false);
                                    }}
                                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${
                                        value === opt.value ? 'text-blue-600 font-medium bg-blue-50/50 dark:bg-blue-900/10' : 'text-slate-600 dark:text-slate-300'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                            <div className="border-t border-slate-100 dark:border-slate-700 my-1"></div>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCustomInput(true);
                                    setCustomInput('');
                                }}
                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 text-blue-500 font-medium transition-colors"
                            >
                                自定义时间...
                            </button>
                        </>
                    )}
                </motion.div>
            </AnimatePresence>,
            document.body
        )}
    </div>
  );
};

export default ReminderPicker;
